import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BottomNav } from "@/components/BottomNav";
import { ChatWidget } from "@/components/ChatWidget";
import AppUpdateDialog from "@/components/AppUpdateDialog";
import Login from "@/pages/Login";
import Markets from "@/pages/Markets";
import Trade from "@/pages/Trade";
import Wallet from "@/pages/Wallet";
import Profile from "@/pages/Profile";
import CoinDetail from "@/pages/CoinDetail";
import Referral from "@/pages/Referral";
import Admin from "@/pages/Admin";
import BlockedScreen from "@/pages/BlockedScreen";
import NativeNavigation from "@/components/NativeNavigation";
import GamePolicy from "@/pages/GamePolicy";
import HowToPlay from "@/pages/HowToPlay";

function SessionGate({ children }) {
  const { connectionError, loading, refresh } = useAuth();
  if (!connectionError || ["/game-policy", "/how-to-play"].includes(window.location.pathname)) return children;
  return <div className="min-h-screen bg-[#0B0E14] flex flex-col items-center justify-center px-6 gap-4 text-center">
    <p role="alert" className="text-sm text-slate-300">{connectionError}</p>
    <button onClick={refresh} disabled={loading} className="rounded-lg px-6 py-3 bg-[#00E676] text-black font-semibold">{loading ? "Reconnecting…" : "Retry connection"}</button>
  </div>;
}

function Shell({ children }) {
  const { pathname } = useLocation();
  const aboveCoinActions = /^\/coin\/(?!USDT\/?$)[^/]+\/?$/i.test(pathname);
  return (
    <div className="min-h-screen bg-[#0B0E14]">
      {children}
      <ChatWidget aboveCoinActions={aboveCoinActions} />
      <BottomNav />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0E14]">
        <Loader2 className="animate-spin text-[#00E676]" size={28} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.blocked) return <BlockedScreen />;
  return <Shell>{children}</Shell>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0E14]">
        <Loader2 className="animate-spin text-[#00E676]" size={28} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0E14]">
        <Loader2 className="animate-spin text-[#00E676]" size={28} />
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppUpdateDialog />
        <SessionGate>
        <BrowserRouter>
          <NativeNavigation />
          <Routes>
            <Route path="/game-policy" element={<GamePolicy />} />
            <Route path="/how-to-play" element={<HowToPlay />} />
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/" element={<Protected><Markets /></Protected>} />
            <Route path="/trade" element={<Protected><Trade /></Protected>} />
            <Route path="/coin/:symbol" element={<Protected><CoinDetail /></Protected>} />
            <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/referral" element={<Protected><Referral /></Protected>} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </SessionGate>
        <Toaster position="top-center" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
